export interface SeriesJson {
	id: number,
	name: string,
	description: string|null,
	image_filename: string|null,
}

export interface SermonJson {
	id: number,
	name: string,
	date: string,
	lang_primary: string,
	lang_secondary: string|null,
	series: number|null,
	description: string|null,
	youtube_id: string,
}

export interface SermonDataJson {
	series: SeriesJson[],
	sermons: SermonJson[],
}



export class Series {
	constructor(
		public readonly id: number,
		public readonly name: string,
		public readonly description: string|null,
		public readonly imageFilename: string|null,
	) { }
}

export class Sermon {
	constructor(
		public readonly id: number,
		public readonly name: string,
		public readonly date: Date,
		public readonly langPrimary: string,
		public readonly langSecondary: string|null,
		public readonly series: Series|null,
		public readonly description: string|null,
		public readonly youtubeId: string,
	) { }
}

export class DataManager {
	public series: Map<number, Series> = new Map();
	public sermons: Map<number, Sermon> = new Map();

	constructor(
		data: Readonly<SermonDataJson>,
	) {
		for (const seriesJson of data.series) {
			const series = new Series(
				seriesJson.id,
				seriesJson.name,
				seriesJson.description,
				seriesJson.image_filename,
			);

			this.series.set(seriesJson.id, series);
		}

		// Sort by date: https://stackoverflow.com/a/18246278
		const dataSermons = data.sermons.sort(
			(a, b) =>
				new Date(b.date).getTime() - new Date(a.date).getTime()
		);

		for (const sermonJson of dataSermons) {
			let series: Series|null;
			if (sermonJson.series !== null) {
				const existingSeries = this.series.get(sermonJson.series);

				if (existingSeries) {
					series = existingSeries;
				} else {
					console.warn(`Could not find series with ID ${sermonJson.series}`);
					series = null;
				}
			} else {
				series = null;
			}

			const sermon = new Sermon(
				sermonJson.id,
				sermonJson.name,
				new Date(sermonJson.date),
				sermonJson.lang_primary,
				sermonJson.lang_secondary,
				series,
				sermonJson.description,
				sermonJson.youtube_id,
			);

			this.sermons.set(sermonJson.id, sermon);
		}
	}

	private memoizedAllSermonsByYear: ReadonlyMap<number, readonly Sermon[]>|undefined = undefined;

	allSermonsByYear(): ReadonlyMap<number, readonly Sermon[]> {
		if (!this.memoizedAllSermonsByYear) {
			this.memoizedAllSermonsByYear = (() => {
				const result: Map<number, Sermon[]> = new Map();

				for (const sermon of this.sermons.values()) {
					const year = sermon.date.getFullYear();

					if (!result.has(year)) {
						result.set(year, []);
					}

					result.get(year)!.push(sermon);
				}

				return result;
			})();
		}

		return this.memoizedAllSermonsByYear;
	}
}